import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import Modal from "../../../../parts/modal-new";
import { STRIPE } from "../../../../constants";
import ModalFooter from "../../../../parts/modals/ModalFooter";
import Select from "../../../../parts/inputs/select";
import Coupon from "./Coupon";

const { SUPPORTED_CURRENCIES } = STRIPE;

export const CreateCoupon = (props, { locals }) => {
  const coupons = locals.stripeCoupon?.coupons ?? [];
  const [filter, setFilter] = useState("valid");
  const [currency, setCurrency] = useState(SUPPORTED_CURRENCIES[0]);
  const [discountType, setDiscountType] = useState("percentage");
  const [duration, setDuration] = useState("once");
  const [dateLimit, setDateLimit] = useState(false);
  const [limitTimes, setLimitTimes] = useState(false);

  const filteredCoupons = useMemo(() => {
    return coupons.filter(({ valid }) => {
      if (filter === "valid") return valid;
      return !valid;
    });
  }, [coupons, filter]);

  return (
    <Modal className="coupon-modal" title="Create Coupon">
      <div className="coupon-modal-container">
        <aside>
          <nav role="tablist">
            <a
              href="#"
              role="button"
              aria-current={filter === "valid" ? true : undefined}
              onClick={(e) => {
                e.preventDefault();
                setFilter("valid");
              }}
            >
              Valid
            </a>
            <a
              href="#"
              role="button"
              aria-current={filter === "expired" ? true : undefined}
              onClick={(e) => {
                e.preventDefault();
                setFilter("expired");
              }}
            >
              Expired
            </a>
          </nav>
          {filteredCoupons.map((coupon) => {
            return <Coupon key={coupon.id} coupon={coupon} />;
          })}
        </aside>
        <form
          className="body"
          action="/actions/plan-manager/create-coupon"
          method="POST"
          id="create-coupon"
        >
          <div className="row">
            <div className="group">
              <label htmlFor="name" className="label">
                Coupon Name
              </label>
              <input type="text" name="name" placeholder="Enter name" />
            </div>
          </div>
          <div className="row">
            <div className="group">
              <label htmlFor="id" className="label">
                Coupon Code
              </label>
              <input type="text" name="id" placeholder="e.g. GETSTRONG2025" />
            </div>
          </div>
          <div className="row">
            <div className="group">
              <label htmlFor="discountType">Discount Type</label>
              <div className="radio-group">
                <input
                  type="radio"
                  name="discountType"
                  id="percentage"
                  value="percentage"
                  checked={discountType === "percentage"}
                  onChange={() => setDiscountType("percentage")}
                />
                <label htmlFor="percentage">Percentage Discount</label>
              </div>
              <div className="radio-group">
                <input
                  type="radio"
                  name="discountType"
                  id="fixed"
                  value="fixed"
                  checked={discountType === "fixed"}
                  onChange={() => setDiscountType("fixed")}
                />
                <label htmlFor="fixed">Fixed Amount Discount</label>
              </div>
            </div>
          </div>
          <div className="row flex gap-2">
            {discountType === "percentage" ? (
              <div className="group flex-1">
                <label htmlFor="percent_off" className="label">
                  Discount Percentage
                </label>
                <input
                  type="number"
                  name="percent_off"
                  placeholder="e.g. 10"
                  required
                  min={0}
                  max={100}
                />
              </div>
            ) : (
              <>
                <div className="group flex-1">
                  <label htmlFor="amount_off" className="label">
                    Discount Amount
                  </label>
                  <input
                    type="number"
                    name="amount_off"
                    placeholder="0.00"
                    required
                    step={0.01}
                    min={0}
                  />
                </div>
                <div className="group flex-1">
                  <label htmlFor="currency" className="label">
                    Currency
                  </label>

                  <Select
                    name="currency"
                    id="currency"
                    options={SUPPORTED_CURRENCIES.map((currency) => {
                      return {
                        value: currency.toLowerCase(),
                        text: currency,
                      };
                    })}
                    value={currency}
                    onChange={(newVal) => setCurrency(newVal)}
                  />
                </div>
              </>
            )}
          </div>
          <div className="row">
            <div className="group">
              <label htmlFor="duration">Duration</label>
              <Select
                name="duration"
                id="duration"
                options={[
                  { value: "once", text: "Once" },
                  { value: "forever", text: "Forever" },
                  { value: "repeating", text: "Repeating" },
                ]}
                value={duration}
                onChange={(newVal) => setDuration(newVal)}
              />
            </div>
          </div>
          {duration === "repeating" && (
            <div className="row">
              <div className="group">
                <label htmlFor="duration_in_months" className="label">
                  Duration in Months
                </label>
                <input
                  type="number"
                  name="duration_in_months"
                  placeholder="e.g. 3"
                  required
                  min={1}
                />
              </div>
            </div>
          )}
          <div className="row">
            <div className="group mb-4">
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  name="dateLimit"
                  id="dateLimit"
                  checked={dateLimit}
                  onChange={() => setDateLimit((prev) => !prev)}
                />
                <label htmlFor="dateLimit">
                  Limit the date range when customers can redeem this coupon
                </label>
              </div>
              {dateLimit && (
                <div className="date-range mt-3">
                  <label htmlFor="redeem_by">Expiry date</label>
                  <input
                    type="datetime-local"
                    name="redeem_by"
                    id="redeem_by"
                    required
                  />
                </div>
              )}
            </div>
            <div className="group">
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  name="limitTimes"
                  id="limitTimes"
                  checked={limitTimes}
                  onChange={() => setLimitTimes((prev) => !prev)}
                />
                <label htmlFor="limitTimes">
                  Limit the total number of times this coupon can be redeemed
                </label>
              </div>
              {limitTimes && (
                <div className="mt-3">
                  <label htmlFor="max_redemptions" className="label">
                    Maximum Redemptions
                  </label>
                  <input
                    type="number"
                    name="max_redemptions"
                    placeholder="e.g. 10"
                    required
                    min={1}
                  />
                </div>
              )}
            </div>
          </div>
        </form>
      </div>

      <ModalFooter
        isSubmitButton
        showAltButton={false}
        onClickButton={() => {}}
        classNameButton="button save"
        formId="create-coupon"
        buttonText="Create"
      />
    </Modal>
  );
};

CreateCoupon.contextTypes = { locals: PropTypes.object };
